<button type="button"
        class="scroll-top-btn"
        *ngIf="showScrollTop()"
        (click)="scrollToTop()"
        title="Scroll to top"
        aria-label="Scroll to top">
  ↑
</button>

<div class="debug-toast" *ngIf="toastMessage() as msg" [class.error]="msg.error">
  {{ msg.text }}
</div>

<div class="role19-debug-root" [formGroup]="form" [class.two-pane]="twoPaneMode()">
  <div class="debug-banner">
    <div class="debug-banner-row">
      <div class="debug-banner-left">
        <span class="debug-badge">DEBUG</span>
        <strong>Role 19 — Refactored Developer Layout</strong>
      </div>
      <div class="debug-banner-right">
        <button type="button"
                class="debug-btn primary"
                [class.mode-save]="anyEditableInEditMode()"
                [disabled]="!host.isSyncValidationComplete() || editableRows().length === 0"
                (click)="toggleEditAll()">
          <ng-container *ngIf="anyEditableInEditMode(); else editAllLabel">
            ✓ Save all edits
          </ng-container>
          <ng-template #editAllLabel>✎ Edit all editable rows</ng-template>
        </button>
        <button type="button"
                class="debug-btn"
                (click)="twoPaneMode.set(!twoPaneMode())"
                [class.active]="twoPaneMode()"
                title="Toggle two-pane mode (Alt+↑/↓ to move selection)">
          ⫴ {{ twoPaneMode() ? 'Stacked view' : 'Two-pane view' }}
        </button>
        <button type="button"
                class="debug-btn"
                (click)="utilityPaneOpen.set(!utilityPaneOpen())"
                [class.active]="utilityPaneOpen()"
                title="Show the paste / column-fill / export utilities">
          🛠 Utilities
        </button>
      </div>
    </div>

    <div class="debug-banner-row">
      <div class="debug-hint">
        <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>D</kbd> toggle ·
        <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>E</kbd> next error ·
        <kbd>Ctrl</kbd>+<kbd>Alt</kbd>+<kbd>Q</kbd> previous error ·
        values capped below 1 trillion ·
        Δ flags Current vs Previous mismatches
      </div>
      <ng-container *ngIf="formStats() as fs">
        <span class="stats-pill" title="FormGroup stats (dirty / touched / disabled / total)">
          <span class="stat stat-dirty">{{ fs.dirty }} dirty</span>
          <span class="stat stat-touched">{{ fs.touched }} touched</span>
          <span class="stat stat-disabled">{{ fs.disabled }} disabled</span>
          <span class="stat">{{ fs.total }} total</span>
        </span>
      </ng-container>
    </div>

    <div class="debug-banner-row filter-row">
      <label class="filter-toggle">
        <input type="checkbox" [checked]="onlyFailing()" (change)="onlyFailing.set(!onlyFailing())" />
        Only failing rows
      </label>
      <label class="filter-toggle">
        <input type="checkbox" [checked]="hideCalculated()" (change)="hideCalculated.set(!hideCalculated())" />
        Hide calculated
      </label>
      <label class="filter-toggle">
        <input type="checkbox" [checked]="hideReadonly()" (change)="hideReadonly.set(!hideReadonly())" />
        Hide readonly
      </label>
      <label class="filter-toggle">
        <input type="checkbox" [checked]="hideZeroValued()" (change)="hideZeroValued.set(!hideZeroValued())" />
        Hide all-zero rows
      </label>
      <label class="find-field" title="Highlights equity columns whose label or qname contains the query">
        <span>Find column:</span>
        <input type="text" [value]="findColumnQuery()" (input)="findColumnQuery.set($any($event.target).value)"
               placeholder="e.g. Issued capital" />
      </label>
    </div>

    <div class="utility-pane" *ngIf="utilityPaneOpen()">
      <div class="utility-group">
        <strong>Fill a column</strong>
        <select [value]="fillTargetColumn()" (change)="fillTargetColumn.set($any($event.target).value)">
          <option value="">Choose column…</option>
          <option *ngFor="let col of filteredColumns(); trackBy: trackByQname" [value]="col.qname">
            {{ col.label }}
          </option>
        </select>
        <input type="number" step="0.01" [value]="fillValue()"
               (input)="fillValue.set($any($event.target).valueAsNumber)" placeholder="value" />
        <button type="button" class="debug-btn" [disabled]="!fillTargetColumn()" (click)="fillColumn()">
          Fill visible rows
        </button>
      </div>
      <div class="utility-group wide">
        <strong>Paste JSON</strong>
        <textarea rows="3" [value]="pasteText()" (input)="pasteText.set($any($event.target).value)"
                  placeholder='{ "IssuedCapital:Component[Consolidated][CurrentYear]": 12000 }'></textarea>
        <div class="utility-row">
          <button type="button" class="debug-btn" (click)="applyPaste()">Apply paste</button>
          <button type="button" class="debug-btn" (click)="exportAllJson()">Copy full form JSON</button>
          <span class="paste-status" *ngIf="pasteStatus()">{{ pasteStatus() }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Stacked layout (default) -->
  <ng-container *ngIf="!twoPaneMode()">
    <ng-container *ngFor="let consolidationType of consolidationTypes(); trackBy: trackByValue">
      <details class="debug-consolidation" open>
        <summary>
          <span class="consolidation-label">{{ consolidationType }}</span>
          <span class="period-label">
            {{ host.periodHeaderCurrent }}
            <ng-container *ngIf="host.hasPreviousYear"> vs {{ host.periodHeaderPrevious }}</ng-container>
          </span>
          <ng-container *ngIf="summaryFor(consolidationType) as summary">
            <span class="summary-chip" [class.ok]="summary.failing === 0">
              <span *ngIf="summary.failing === 0">✓ all clear</span>
              <span *ngIf="summary.failing > 0">⚠ {{ summary.failing }} rows failing</span>
              <span class="sub-chip" *ngIf="summary.required">{{ summary.required }} required</span>
              <span class="sub-chip" *ngIf="summary.crossSheet">{{ summary.crossSheet }} cross-sheet</span>
              <span class="sub-chip" *ngIf="summary.columnSum">{{ summary.columnSum }} col-sum</span>
              <span class="sub-chip" *ngIf="summary.equityTotal">{{ summary.equityTotal }} equity-total</span>
              <span class="sub-chip" *ngIf="summary.beginEnd">{{ summary.beginEnd }} begin/end</span>
            </span>
          </ng-container>
        </summary>

        <ng-container *ngFor="let row of visibleDataRows(); trackBy: trackByQname">
          <ng-container *ngIf="shouldRenderRow(row, consolidationType)">
            <ng-container *ngTemplateOutlet="rowCardTpl;
              context: { $implicit: row, consolidationType: consolidationType }"></ng-container>
          </ng-container>
        </ng-container>
      </details>
    </ng-container>
  </ng-container>

  <!-- Two-pane layout -->
  <ng-container *ngIf="twoPaneMode()">
    <div class="two-pane-grid">
      <aside class="pane-list">
        <header class="pane-list-header">
          Rows
          <span class="muted">({{ paneRows().length }})</span>
        </header>
        <button type="button"
                class="pane-list-item"
                *ngFor="let row of paneRows(); trackBy: trackByQname"
                [attr.data-row-qname]="row.qname"
                [class.selected]="selectedRowQname() === row.qname"
                [class.failed]="rowFailed(row, 'Separate') || rowFailed(row, 'Consolidated')"
                (click)="selectRow(row)">
          <span class="pane-list-label" [innerHTML]="row.label | negativeLabel"></span>
          <span *ngIf="host.isCalculatedField(row)" class="badge badge-calc">calc</span>
          <span *ngIf="row.ui.readonly && !host.isCalculatedField(row)" class="badge badge-readonly">ro</span>
        </button>
      </aside>

      <section class="pane-detail">
        <ng-container *ngIf="selectedRow() as row; else pickRow">
          <ng-container *ngFor="let consolidationType of consolidationTypes(); trackBy: trackByValue">
            <ng-container *ngIf="host.shouldShowRow(row, rowIndex(row), consolidationType, 'Current')">
              <ng-container *ngTemplateOutlet="rowCardTpl;
                context: { $implicit: row, consolidationType: consolidationType }"></ng-container>
            </ng-container>
          </ng-container>
        </ng-container>
        <ng-template #pickRow>
          <div class="pane-empty">Pick a row on the left — <kbd>Alt</kbd>+<kbd>↑</kbd>/<kbd>↓</kbd> to move.</div>
        </ng-template>
      </section>
    </div>
  </ng-container>

  <!-- ─── Reusable row card template ─────────────────────────────── -->
  <!-- Declared INSIDE the [formGroup] wrapper so the template's
       declaration-time DI context includes the FormGroupDirective.
       *ngTemplateOutlet renders the embedded view using that context,
       so formControlName inside the card can resolve to `form`. -->
  <ng-template #rowCardTpl let-row let-consolidationType="consolidationType">
    <details class="debug-row-card"
             [attr.data-row-qname]="row.qname"
             [class.row-failed]="rowFailed(row, consolidationType)"
             open>
    <summary class="debug-row-header">
      <span class="row-collapse-chevron" aria-hidden="true">▸</span>
      <span class="consolidation-badge">{{ consolidationType }}</span>
      <span class="row-period-label">
        {{ host.periodHeaderCurrent }}<ng-container *ngIf="host.hasPreviousYear"> vs {{ host.periodHeaderPrevious }}</ng-container>
      </span>
      <span class="row-label" [innerHTML]="row.label | negativeLabel"></span>
      <span *ngIf="host.isCalculatedField(row)" class="badge badge-calc" title="Calculated row">calc</span>
      <span *ngIf="row.ui.readonly && !host.isCalculatedField(row)" class="badge badge-readonly">readonly</span>
      <span class="row-status"
            [class.status-failed]="host.getRowValidationStatus(row, consolidationType, 'Current') === 'Failed'">
        {{ host.getRowValidationStatus(row, consolidationType, 'Current') }}
      </span>
      <ng-container *ngIf="host.hasPreviousYear">
        <span class="row-status-sep">/</span>
        <span class="row-status"
              [class.status-failed]="host.getRowValidationStatus(row, consolidationType, 'Previous') === 'Failed'">
          {{ host.getRowValidationStatus(row, consolidationType, 'Previous') }}
        </span>
      </ng-container>
      <span class="row-qname">{{ row.qname }}</span>
      <span class="row-edit-actions"
            *ngIf="host.isSyncValidationComplete()"
            (click)="$event.stopPropagation()">
        <ng-container *ngIf="isEditable(row)">
          <button type="button"
                  class="row-action-btn"
                  *ngIf="!isRowInEditModeForConsolidation(row, consolidationType)"
                  (click)="enterEdit(row, consolidationType)"
                  title="Edit this row">✎ Edit</button>
          <ng-container *ngIf="isRowInEditModeForConsolidation(row, consolidationType)">
            <button type="button" class="row-action-btn row-action-save"
                    (click)="saveEdit(row, consolidationType)" title="Save">✓ Save</button>
            <button type="button" class="row-action-btn row-action-cancel"
                    (click)="cancelEdit(row, consolidationType)" title="Cancel">✕ Cancel</button>
            <button type="button" class="row-action-btn"
                    (click)="promptFillRow(row)"
                    title="Fill every non-calculated cell of this row (snapshots first)">▦ Fill…</button>
            <button type="button" class="row-action-btn"
                    (click)="zeroRow(row)"
                    title="Set every non-calculated cell to 0.00 (snapshots first)">0⃣ Zero</button>
          </ng-container>
          <button type="button" class="row-action-btn"
                  *ngIf="hasUndo(row)"
                  (click)="undoRow(row)"
                  title="Revert the most recent fill / zero">↶ Undo</button>
        </ng-container>
        <button type="button" class="row-action-btn"
                (click)="copyRowJson(row)"
                title="Copy this row's values (across all 4 tables) as JSON">⎘ Copy</button>
      </span>
    </summary>

    <div class="debug-cells-wrapper">
      <table class="debug-cells">
        <thead>
          <tr>
            <th class="col-name">Equity Column</th>
            <th class="col-val">{{ host.periodHeaderCurrent }} <span class="muted">(Current Year)</span></th>
            <th *ngIf="host.hasPreviousYear" class="col-val">{{ host.periodHeaderPrevious }} <span class="muted">(Previous Year)</span></th>
            <th *ngIf="host.hasPreviousYear" class="col-delta">Δ</th>
            <th class="col-errors">Errors</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let column of filteredColumns(); trackBy: trackByQname"
              class="debug-cell-row">
            <td class="col-name" [title]="column.qname">
              {{ column.label }}
              <ng-container *ngIf="columnSumBadge(column) as badge">
                <span class="eq-badge"
                      [class.eq-subtotal]="badge.role === 'subtotal'"
                      [class.eq-component]="badge.role === 'component'"
                      [title]="badge.tooltip">Σ</span>
              </ng-container>
            </td>

            <td class="col-val"
                [class.cell-has-error]="cellErrors(row, column, consolidationType, 'Current').length > 0">
              <p-inputnumber appNegativeHighlight
                [formControlName]="host.getEquityColumnControlName(row.qname, column.qname, consolidationType, 'Current')"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [max]="maxValue"
                [useGrouping]="true"
                inputStyleClass="debug-compact-input">
              </p-inputnumber>
            </td>

            <td *ngIf="host.hasPreviousYear" class="col-val"
                [class.cell-has-error]="cellErrors(row, column, consolidationType, 'Previous').length > 0">
              <p-inputnumber appNegativeHighlight
                [formControlName]="host.getEquityColumnControlName(row.qname, column.qname, consolidationType, 'Previous')"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [max]="maxValue"
                [useGrouping]="true"
                inputStyleClass="debug-compact-input">
              </p-inputnumber>
            </td>

            <td *ngIf="host.hasPreviousYear" class="col-delta">
              <ng-container *ngIf="delta(row, column, consolidationType) as d; else dashDelta">
                <span [class.delta-nonzero]="d !== 0" [class.delta-zero]="d === 0">
                  {{ d | number: '1.2-2' }}
                </span>
              </ng-container>
              <ng-template #dashDelta>
                <span class="muted">—</span>
              </ng-template>
            </td>

            <td class="col-errors">
              <ng-container *ngFor="let err of cellErrors(row, column, consolidationType, 'Current')">
                <button type="button"
                        class="err-chip-btn"
                        (click)="revealErrorDetails(consolidationType, row, 'Current')"
                        title="Jump to the full Current Year error description">
                  <p-message severity="error" [text]="'[Current Year] ' + err" styleClass="debug-err"></p-message>
                </button>
              </ng-container>
              <ng-container *ngIf="host.hasPreviousYear">
                <ng-container *ngFor="let err of cellErrors(row, column, consolidationType, 'Previous')">
                  <button type="button"
                          class="err-chip-btn"
                          (click)="revealErrorDetails(consolidationType, row, 'Previous')"
                          title="Jump to the full Previous Year error description">
                    <p-message severity="error" [text]="'[Previous Year] ' + err" styleClass="debug-err"></p-message>
                  </button>
                </ng-container>
              </ng-container>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ng-container *ngIf="host.isSyncValidationComplete()">
      <details *ngIf="host.getRowErrorDescription(row, consolidationType, 'Current') as curDesc"
               [id]="errorDetailsId(consolidationType, row, 'Current')"
               class="row-error-desc">
        <summary>
          <span class="error-tag">Current Year</span>
          <span class="error-expand-hint">click to expand</span>
        </summary>
        <div class="error-full" [innerHTML]="curDesc"></div>
      </details>
      <details *ngIf="host.hasPreviousYear && host.getRowErrorDescription(row, consolidationType, 'Previous') as prevDesc"
               [id]="errorDetailsId(consolidationType, row, 'Previous')"
               class="row-error-desc">
        <summary>
          <span class="error-tag">Previous Year</span>
          <span class="error-expand-hint">click to expand</span>
        </summary>
        <div class="error-full" [innerHTML]="prevDesc"></div>
      </details>
    </ng-container>
  </details>
  </ng-template>
</div>
